<script lang="ts" setup>
export type HeaderIconColor = 'blue' | 'cyan' | 'teal' | 'purple' | 'gold' | 'amber' | 'green' | 'red' | 'orange'

defineProps<{
  /** Page title */
  title: string
  /** Page subtitle */
  subtitle?: string
  /** Icon name (mdi-* format) */
  icon: string
  /** Icon color variant */
  iconColor?: HeaderIconColor
  /** Show back button */
  showBack?: boolean
  /** Custom back button handler (uses router.back() if not provided) */
  onBack?: () => void
}>()

defineEmits<{
  (e: 'back'): void
}>()

</script>

<template>
  <header class="dashboard-page-header">
    <div class="dashboard-header-content">
      <!-- Back Button -->
      <button
        v-if="showBack"
        type="button"
        class="dashboard-back-link"
        @click="onBack ? onBack() : $emit('back')"
      >
        <v-icon icon="mdi-arrow-left" size="20" />
      </button>

      <!-- Icon -->
      <div
        class="dashboard-header-icon"
        :class="iconColor ? `dashboard-header-icon--${iconColor}` : 'dashboard-header-icon--blue'"
      >
        <v-icon :icon="icon" size="28" />
      </div>

      <!-- Text -->
      <div class="dashboard-header-text">
        <h1 class="dashboard-page-title">{{ title }}</h1>
        <p v-if="subtitle" class="dashboard-page-subtitle">{{ subtitle }}</p>
      </div>
    </div>

    <!-- Actions slot (outside header-content for space-between alignment) -->
    <slot name="actions" />

    <!-- Below header slot (for tabs, filters, etc.) -->
    <slot name="below" />
  </header>
</template>

<style lang="scss" scoped>
/* Component uses global dashboard-* classes from dashboard-common.css */
/* Only component-specific adjustments here */

.dashboard-page-header {
  margin-bottom: var(--dashboard-header-margin);
}

.dashboard-header-content {
  display: flex;
  align-items: center;
  gap: var(--dashboard-gap-lg);
}

.dashboard-header-text {
  flex: 1;
}
</style>
